import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { WhatsappLogo } from "@phosphor-icons/react";
import { buildWhatsAppUrl, trackWhatsAppClick } from "../lib/whatsapp";
import { buildLocalizedPath } from "../lib/langRoutes";

export const Header = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const lang = i18n.language?.split("-")[0] || "de";

  const NAV = [
    { to: buildLocalizedPath("treatments", lang), key: "treatments" },
    { to: buildLocalizedPath("about", lang), key: "about" },
    { to: buildLocalizedPath("prices", lang), key: "prices" },
    { to: buildLocalizedPath("faq", lang), key: "faq" },
  ];
  const homePath = buildLocalizedPath("home", lang);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F6F5F2]/90 backdrop-blur-xl border-b border-[#E4E2DA]"
          : "bg-[#F6F5F2]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 lg:h-28 flex items-center justify-between gap-6">
        <Link to={homePath} data-testid="header-logo" className="flex items-center" aria-label="Kosmedico Zürich">
          <img src="/kosmedico-logo.svg" alt="Kosmedico" className="h-16 md:h-20 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {NAV.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={`text-sm font-light transition-colors relative ${
                  active ? "text-[#3E5239]" : "text-[#3E5239]/80 hover:text-[#3E5239]"
                } after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-[#A7CA99] after:transition-all after:duration-300 ${
                  active ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-5">
          <LanguageSwitcher />
          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("header_cta")}
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center gap-2 px-5 lg:px-6 py-3 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
          >
            <WhatsappLogo size={16} weight="fill" />
            {t("nav.book")}
          </a>
          <button
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#3E5239] text-xl"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F6F5F2] border-t border-[#E4E2DA] px-6 py-6 space-y-4">
          {NAV.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.key}`}
              className="block text-[#3E5239] hover:text-[#A7CA99] text-base font-light"
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("mobile_header_cta")}
            data-testid="mobile-cta-button"
            className="inline-flex items-center gap-2 mt-2 px-6 py-3 bg-[#25D366] text-white text-xs uppercase tracking-[0.18em]"
          >
            <WhatsappLogo size={16} weight="fill" />
            {t("nav.book")}
          </a>
        </div>
      )}
    </header>
  );
};
